import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import api from 'state/axios';
import { fetchCrewRanks } from 'state/api';
import FlexBetween from 'components/FlexBetween';


const BLANK = { ranks: [], bases: [], employeeCode: '', employeeName: '' };

const MatrixFilterDialog = ({ open, filters, onApply, onClose }) => {
  const [draft, setDraft] = useState(BLANK);
  const [rankOptions, setRankOptions] = useState([]);
  const [baseOptions, setBaseOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({ ...filters });
    setLoading(true);
    Promise.all([
      fetchCrewRanks(),
      api.get('/employees/bases').then((r) => r.data),
    ])
      .then(([ranks, bases]) => {
        setRankOptions(ranks.map((r) => r.rankCode));
        setBaseOptions(bases);
      })
      .catch(() => toast.error('Failed to load filter options.'))
      .finally(() => setLoading(false));
  }, [open, filters]);

  const toggle = (field, value) =>
    setDraft((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));

  const activeCount =
    draft.ranks.length +
    draft.bases.length +
    (draft.employeeCode ? 1 : 0) +
    (draft.employeeName ? 1 : 0);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <FlexBetween>
          <Typography 
            variant="subtitle1" 
            fontWeight="bold"
          >
            Filter
          </Typography>
          <IconButton 
            size="small" 
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </FlexBetween>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {rankOptions.length > 0 && (
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block" 
                  mb={0.75}
                >
                  Rank
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {rankOptions.map((rank) => (
                    <Chip
                      key={rank}
                      label={rank}
                      size="small"
                      onClick={() => toggle('ranks', rank)}
                      variant={draft.ranks.includes(rank) ? 'filled' : 'outlined'}
                      color={draft.ranks.includes(rank) ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {baseOptions.length > 0 && (
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  display="block" 
                  mb={0.75}
                >
                  Base
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={0.75}>
                  {baseOptions.map((base) => (
                    <Chip
                      key={base}
                      label={base}
                      size="small"
                      onClick={() => toggle('bases', base)}
                      variant={draft.bases.includes(base) ? 'filled' : 'outlined'}
                      color={draft.bases.includes(base) ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <TextField
              size="small"
              label="Employee Code"
              value={draft.employeeCode}
              onChange={(e) => setDraft((prev) => ({ ...prev, employeeCode: e.target.value }))}
              placeholder="Partial match"
              fullWidth
            />

            <TextField
              size="small"
              label="Employee Name"
              value={draft.employeeName}
              onChange={(e) => setDraft((prev) => ({ ...prev, employeeName: e.target.value }))}
              placeholder="Partial match"
              fullWidth
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button
          size="small"
          color="inherit"
          onClick={() => setDraft(BLANK)}
          disabled={activeCount === 0}
        >
          Reset
        </Button>
        <Box flex={1} />
        <Button 
          size="small" 
          variant="outlined" 
          color='secondary'
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={handleApply}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatrixFilterDialog;
